import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Wordmark } from "@/components/Logo";
import { PaymentMethodPicker } from "@/components/PaymentMethodPicker";
import { api, errMsg, fmtDate, money } from "@/lib/api";

export default function Pay() {
  const { token } = useParams();
  const [sp] = useSearchParams();
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/pay/${token}`).then((r) => setInv(r.data)).catch((e) => setErr(errMsg(e)));
  }, [token]);

  const [cashDone, setCashDone] = useState(false);
  const pay = async (method, note) => {
    setBusy(true);
    setErr("");
    try {
      if (method === "cash") {
        await api.post(`/pay/${token}/cash`, { note });
        setCashDone(true);
        setBusy(false);
        return;
      }
      const { data } = await api.post(`/pay/${token}/checkout`, { origin_url: window.location.origin, method });
      window.location.href = data.checkout_url;
    } catch (e) {
      setErr(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF7FB] px-5 py-10" data-testid="pay-page">
      <div className="mx-auto max-w-lg">
        <Wordmark />
        {err && <p className="mt-16 text-red-300" data-testid="pay-error">{err}</p>}
        {sp.get("cancelled") && <p className="mt-8 text-amber-300 text-sm" data-testid="pay-cancelled">Payment cancelled — you can try again.</p>}
        {inv && (
          <div className="mt-12 rounded-3xl border border-[#2A1240]/10 bg-[#FFFFFF] p-8">
            <p className="kicker">{inv.kind} · {inv.number}</p>
            <h1 className="mt-3 font-display text-3xl text-[#2A1240]" data-testid="pay-title">{inv.title}</h1>
            <p className="mt-2 text-sm text-slate-400">From {inv.company_name} to {inv.payer_name}{inv.due_date && ` · due ${fmtDate(inv.due_date)}`}</p>
            {inv.description && <p className="mt-4 text-sm text-slate-300">{inv.description}</p>}
            <div className="mt-8 space-y-3 font-mono text-sm">
              <div className="flex justify-between text-slate-300"><span>Amount</span><span>{money(inv.amount, 2)}</span></div>
              <div className="flex justify-between text-slate-300"><span>Transaction fee</span><span>{money(inv.fee, 2)}</span></div>
              <div className="flex justify-between border-t border-[#2A1240]/10 pt-3 text-lg text-[#2A1240]"><span>Total</span><span data-testid="pay-total">{money(inv.total, 2)}</span></div>
            </div>
            {inv.status === "paid" ? (
              <p className="mt-8 rounded-xl bg-emerald-400/10 border border-emerald-400/30 p-4 text-emerald-300 text-sm" data-testid="pay-already-paid">Paid{inv.payment_method ? ` by ${inv.payment_method}` : ""} · receipt {inv.receipt_number}</p>
            ) : cashDone || inv.status === "cash_pending" ? (
              <p className="mt-8 rounded-xl bg-amber-300/10 border border-amber-300/30 p-4 text-amber-200 text-sm" data-testid="pay-cash-pending">Cash payment reported — waiting for {inv.company_name} to confirm. Your receipt arrives by email once they do.</p>
            ) : (
              <div className="mt-8"><PaymentMethodPicker total={inv.total} amount={inv.amount} busy={busy} onPay={pay} /></div>
            )}
            <p className="mt-4 text-xs text-slate-400 text-center">Card and crypto are processed by Stripe. Payment details never touch Balqis Book.</p>
          </div>
        )}
      </div>
    </div>
  );
}
